import type { JobFinancialSummary } from '@/hooks/jobs/useJobFinancialSummary';
import type { Job } from '@/types/job';

/**
 * How much of a job's deposit is in, clearing, and still owed.
 *
 * A deposit is collected in parts, so "is it paid?" is not the job's flag
 * alone: settled parts can cover it before the flag says so (an estimate
 * deposit moved onto the job, a legacy row), and money still clearing (an
 * accepted bank debit) is neither paid nor due — it can still be returned.
 *
 * The Payments section quotes these figures in two places — the "Deposit
 * requested" row and the Record payment dialog's deposit mode — so the
 * arithmetic lives here rather than being re-derived per surface.
 */
export type JobDepositCoverage = {
  /** The deposit the job asks for; 0 when it has none. */
  depositAmount: number;
  /** Settled (held or applied) deposit money. */
  depositPaid: number;
  /** Deposit bank debits still clearing; not part of `depositPaid`. */
  depositClearing: number;
  /** Nothing more to collect: settled parts cover it, or the job says so. */
  depositCovered: boolean;
  /**
   * What is left to collect: not paid, not clearing, and never more than the
   * job itself still owes when that is known.
   */
  depositDue: number;
};

/**
 * `owedOnJob` is what the job still owes, when it is known: a priced one-off job
 * with its summary loaded. A deposit is part of the job's price, so once other
 * payments cover the job there is no deposit left to ask for — a refunded
 * deposit on a job paid in full otherwise kept "Deposit requested · Due" on the
 * page. Omit it where the balance is unknown (an unpriced job reads 0 owed,
 * which is not "paid").
 */
export function jobDepositCoverage(
  job: Pick<Job, 'depositAmount' | 'depositReceived'>,
  summary: Pick<JobFinancialSummary, 'depositPaid' | 'depositClearing'> | null | undefined,
  owedOnJob?: number | null,
): JobDepositCoverage {
  const depositAmount = Number(job.depositAmount ?? 0);
  const depositPaid = Number(summary?.depositPaid ?? 0);
  const depositClearing = Number(summary?.depositClearing ?? 0);
  const depositCovered =
    Boolean(job.depositReceived) ||
    (depositAmount > 0 && Math.round(depositPaid * 100) >= Math.round(depositAmount * 100));
  const unpaid =
    depositCovered || !(depositAmount > 0)
      ? 0
      : Math.max(0, Math.round((depositAmount - depositPaid - depositClearing) * 100) / 100);
  const depositDue =
    typeof owedOnJob === 'number' && Number.isFinite(owedOnJob)
      ? Math.min(unpaid, Math.max(0, owedOnJob))
      : unpaid;
  return { depositAmount, depositPaid, depositClearing, depositCovered, depositDue };
}
